import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import PublicLayout from "../components/layout/PublicLayout";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Loader2, XCircle, Upload, ShieldCheck, Phone, MessageCircle, Mail } from "lucide-react";

export default function GuestVerify() {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("booking_id");
  const token = searchParams.get("token");
  const sessionId = searchParams.get("session_id");

  const [paymentStatus, setPaymentStatus] = useState("pending");
  const [kycStatus, setKycStatus] = useState("idle"); // idle | uploading | done | error
  const [docType, setDocType] = useState("national_id");
  const [file, setFile] = useState(null);
  const [booking, setBooking] = useState(null);
  const cancelled = useRef(false);

  // Poll payment status
  useEffect(() => {
    if (!sessionId) { setPaymentStatus("failed"); return; }
    const poll = async (n = 0) => {
      if (cancelled.current) return;
      if (n > 20) { setPaymentStatus("pending"); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setPaymentStatus("paid"); return; }
        if (data.payment_status === "failed") { setPaymentStatus("failed"); return; }
      } catch(e) {}
      setTimeout(() => poll(n + 1), 2500);
    };
    poll();
    return () => { cancelled.current = true; };
  }, [sessionId]);

  // Fetch booking after payment confirmed
  useEffect(() => {
    if (paymentStatus === "paid" && bookingId && token) {
      api.get(`/guest/booking/${bookingId}?token=${token}`)
        .then(({ data }) => setBooking(data))
        .catch(() => {});
    }
  }, [paymentStatus, bookingId, token]);

  const handleUpload = async () => {
    if (!file) { toast.error("Please select a document"); return; }
    setKycStatus("uploading");
    try {
      const formData = new FormData();
      formData.append("booking_id", bookingId);
      formData.append("guest_token", token);
      formData.append("document_type", docType);
      formData.append("file", file);
      await api.post("/guest/kyc", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setKycStatus("done");
      toast.success("Identity verified! Your booking is confirmed.");
      // Re-fetch booking to get owner contact
      try {
        const { data: updatedBooking } = await api.get(`/guest/booking/${bookingId}?token=${token}`);
        setBooking(updatedBooking);
      } catch(_) {}
    } catch(e) {
      toast.error(formatApiError(e));
      setKycStatus("error");
    }
  };

  return (
    <PublicLayout>
      <div className="mx-auto max-w-xl px-4 py-20">
        <Card className="border-border shadow-organic p-10 text-center">

          {/* Payment pending */}
          {paymentStatus === "pending" && (
            <>
              <Loader2 className="mx-auto animate-spin text-primary" size={36} />
              <h1 className="font-display text-2xl font-semibold tracking-tight mt-4">Waiting for payment…</h1>
              <p className="text-sm text-muted-foreground mt-2">Please approve the MTN MoMo request on your phone.</p>
            </>
          )}

          {/* Payment failed */}
          {paymentStatus === "failed" && (
            <>
              <XCircle className="mx-auto text-destructive" size={42} />
              <h1 className="font-display text-2xl font-semibold tracking-tight mt-4">Payment failed</h1>
              <p className="text-sm text-muted-foreground mt-2">Your payment was not completed. Please try again.</p>
              <Button asChild className="mt-6 bg-primary hover:bg-primary/90">
                <Link to="/cars">Back to cars</Link>
              </Button>
            </>
          )}

          {/* Payment confirmed — KYC upload */}
          {paymentStatus === "paid" && kycStatus !== "done" && (
            <>
              <CheckCircle2 className="mx-auto text-primary" size={42} />
              <h1 className="font-display text-2xl font-semibold tracking-tight mt-4">Booking fee paid!</h1>
              <p className="text-sm text-muted-foreground mt-2 mb-6">
                Your dates are secured. Upload your ID to confirm the booking and unlock the owner contact.
                <span className="block mt-1 text-amber-600 font-medium">You have 24 hours to complete this step.</span>
              </p>

              <div className="text-left space-y-4">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold block mb-1.5">Document type</label>
                  <div className="flex gap-2 flex-wrap">
                    {[["national_id","National ID"],["passport","Passport"],["driving_licence","Driving Licence"]].map(([val,label]) => (
                      <button key={val} type="button" onClick={() => setDocType(val)}
                        className={"px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors " + (docType === val ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:bg-muted")}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold block mb-1.5">Upload document</label>
                  <div className={"border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors " + (file ? "border-primary bg-primary/5" : "border-border hover:border-primary/50")}
                    onClick={() => document.getElementById("kyc-file").click()}>
                    <Upload size={24} className="mx-auto text-muted-foreground mb-2" />
                    {file ? (
                      <p className="text-sm font-medium text-primary">{file.name}</p>
                    ) : (
                      <p className="text-sm text-muted-foreground">Click to upload photo or PDF</p>
                    )}
                    <input id="kyc-file" type="file" accept="image/*,.pdf" className="hidden"
                      onChange={e => setFile(e.target.files[0])} />
                  </div>
                </div>

                <Button onClick={handleUpload} disabled={kycStatus === "uploading" || !file}
                  className="w-full h-11 bg-primary hover:bg-primary/90">
                  {kycStatus === "uploading" ? (
                    <><Loader2 size={16} className="mr-2 animate-spin" /> Uploading…</>
                  ) : (
                    <><ShieldCheck size={16} className="mr-2" /> Verify my identity</>
                  )}
                </Button>

                {kycStatus === "error" && (
                  <p className="text-xs text-destructive text-center">Upload failed. Please try again.</p>
                )}
              </div>
            </>
          )}

          {/* KYC done */}
          {kycStatus === "done" && (
            <>
              <CheckCircle2 className="mx-auto text-primary" size={42} />
              <h1 className="font-display text-2xl font-semibold tracking-tight mt-4">Booking confirmed!</h1>
              <p className="text-sm text-muted-foreground mt-2">The car owner has been notified. Check your email for booking details.</p>
              {booking?.owner && (
                <div className="mt-4 p-4 bg-muted/50 rounded-xl text-left">
                  <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Owner contact</div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-bold shrink-0">
                      {booking.owner.name?.charAt(0)?.toUpperCase() || "O"}
                    </div>
                    <div className="font-semibold">{booking.owner.name}</div>
                  </div>
                  <div className="flex gap-2">
                    {booking.owner.phone && (
                      <a href={`https://wa.me/${booking.owner.phone.replace(/[^0-9]/g,"")}`} target="_blank" rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white rounded-xl py-2.5 text-sm font-semibold">
                        <MessageCircle size={14} /> WhatsApp
                      </a>
                    )}
                    {booking.owner.phone && (
                      <a href={`tel:${booking.owner.phone}`}
                        className="flex-1 flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white rounded-xl py-2.5 text-sm font-semibold">
                        <Phone size={14} /> Call
                      </a>
                    )}
                  </div>
                  {booking.owner.email && (
                    <a href={`mailto:${booking.owner.email}`}
                      className="mt-2 flex items-center justify-center gap-2 border border-border hover:bg-muted text-foreground rounded-xl py-2.5 text-sm font-semibold">
                      <Mail size={14} /> Email
                    </a>
                  )}
                </div>
              )}
              <div className="mt-6 p-4 bg-muted rounded-xl text-left">
                <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Want to track your booking?</div>
                <p className="text-sm text-muted-foreground mb-3">Create a free account to manage your bookings, view history, and negotiate prices faster next time.</p>
                <Button asChild className="w-full bg-primary hover:bg-primary/90">
                  <Link to={`/register?email=${booking?.customer_email || ""}&name=${booking?.customer_name || ""}`}>
                    Create free account
                  </Link>
                </Button>
                <Button asChild variant="ghost" className="w-full mt-2">
                  <Link to="/cars">Continue browsing</Link>
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </PublicLayout>
  );
}
